import { Box, Container, Skeleton, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { formatDateRangeLabel, useTablesInput } from 'providers/TablesInputProvider';
import { lazy, ReactNode, Suspense, useState } from 'react';
import { useNavigate } from 'react-router';
import dayjs from 'services/dayjs';
import { UseQueryExecute, UseQueryState } from 'urql';
import ActionButton from 'components/ActionButton';
import { EmptyTableState } from 'components/EmptyTableState';
import { ExpandableTablePlaceholder as ExpandableTablePlaceholderNew } from 'components/ExpandableTable/ExpandableTablePlaceholder';
import KebabMenu from 'components/KebabMenu';
import QueryResult from 'components/QueryResult';
import { SearchAction } from 'components/SearchBar';
import getOnboardingState from 'components/onboarding/get-onboarding-state';
import { HERO_COVERS, HeroPage } from 'config/hero-covers';
import useIsViewport from 'hooks/useIsViewport';
import useNumberParams from 'hooks/useNumberParams';
import useUser from 'hooks/useUser';
import { ButtonColor, ButtonVariant, TablesScreen as TablesScreenType } from 'generated/graphql';
import TableGroup from './TableGroup';
import { TableScreenQueryParam } from './tables-screen-input';
import { useGroup, useTab } from './useTableNodes';

const Hero = lazy(() => import('components/Hero'));
const DateRangePickerDialog = lazy(() => import('components/DateRangePickerDialog'));
const ActionAlertCard = lazy(() => import('components/ActionAlertCard'));

type TablesScreenProps = {
  result: UseQueryState<any>;
  refetch: UseQueryExecute;
  dataKey: string;
  heroPage: HeroPage;
  enableTabs?: boolean;
  // Screen-specific hero content beside the search field, e.g. a buyer selector.
  heroBeforeSearch?: ReactNode;
  heroChildrenMobile?: ReactNode;
  /**
   * The screen's own action, rendered in the hero: top right beside the title
   * on a desktop, and in the header's icon row on a phone. Pass something that
   * collapses to an icon, since the mobile header has no room for words.
   */
  tableActions?: ReactNode;
  // Placeholder for the hero's search field, where "Search" alone is vague.
  searchPlaceholder?: string;
};

const TablesScreen = ({
  result,
  refetch,
  dataKey,
  heroPage,
  enableTabs = true,
  heroBeforeSearch,
  heroChildrenMobile,
  tableActions,
  searchPlaceholder,
}: TablesScreenProps) => {
  const isMobile = useIsViewport();
  const { search, setSearch, dateRange, setDateRange, resetDateRange, setActiveTab, setActiveGroup } = useTablesInput();
  const { user } = useUser();

  const data: TablesScreenType | undefined = result.data?.[dataKey];
  const showLoading = result.fetching && !data;

  const navigate = useNavigate();
  const [getNumberParam] = useNumberParams();

  const tabIndex = getNumberParam(TableScreenQueryParam.Tab) ?? 0;
  const tableGroupIndex = getNumberParam(TableScreenQueryParam.Group) ?? 0;

  // A lazy tab or group loads on its own once the user opens it. Until it has,
  // it shows as the screen declares it: its title, buttons and filters, but
  // none of its tables.
  const tab = useTab(data?.tabs[tabIndex], () => refetch());
  const group = useGroup(tab.node?.tableGroups[tableGroupIndex], tab.refresh);
  const currentTab = tab.node;
  const currentTableGroup = group.node;
  const isLoadingTables = showLoading || tab.isLoading || group.isLoading;
  // Until the user picks dates, the server loads the group in its default
  // ones, so the search bar shows those, with nothing to clear.
  const isDateRangePicked = Boolean(dateRange.startDate && dateRange.endDate);
  const defaultDates = currentTableGroup?.defaultDates;
  const shownDateRange =
    isDateRangePicked || !defaultDates
      ? dateRange
      : { startDate: dayjs(defaultDates.start), endDate: dayjs(defaultDates.end) };

  const [showDateRangePicker, setShowDateRangePicker] = useState(false);

  const [firstTabButton, ...overflowTabButtons] = currentTab?.buttons ?? [];

  // The server leaves out a table with nothing in it, so a group left with no
  // display shows its own placeholder.
  const isEmpty = !currentTableGroup?.data?.length;

  const onboarding = getOnboardingState(user);

  if (onboarding) {
    return onboarding;
  }

  return (
    <QueryResult result={result} empty="Could not load tables" isProgressiveLoadingEnabled>
      <Container sx={(theme) => ({ marginBottom: theme.spacing(10) })}>
        {enableTabs && (
          <Hero
            title={data?.title ?? 'Hi, …'}
            cover={HERO_COVERS[heroPage]}
            subtitle={data?.subTitle}
            tab={tabIndex}
            onTabChange={(_: any, tabIndex: number) => {
              const tab = data?.tabs[tabIndex];
              if (tab?.url) {
                navigate(tab.url);
              } else {
                setActiveTab(tabIndex);
              }
            }}
            newTabGroups={data?.tabs.map((tab) => tab.isNewGroup)}
            tabs={data?.tabs.map((tab) =>
              tab.quantity === null || tab.quantity === undefined ? tab.title : `${tab.title} (${tab.quantity})`,
            )}
            childrenMobile={heroChildrenMobile}
            searchProps={
              currentTableGroup?.isSearchable
                ? {
                    value: search,
                    placeholder: searchPlaceholder,
                    onChange: setSearch,
                    action: currentTableGroup.isDated
                      ? {
                          variant: SearchAction.DateRange,
                          onClick: () => {
                            setShowDateRangePicker(!showDateRangePicker);
                          },
                          color: 'primary',
                          chipLabel: formatDateRangeLabel(shownDateRange.startDate, shownDateRange.endDate),
                          onClear: isDateRangePicked ? resetDateRange : undefined,
                        }
                      : undefined,
                  }
                : undefined
            }
            isLoading={showLoading}
            beforeSearch={
              <>
                {firstTabButton && (
                  <ActionButton
                    {...firstTabButton}
                    // Filled orange whatever the server sends: the header is
                    // dark, and an outlined or primary button disappears on it.
                    color={ButtonColor.Info}
                    variant={ButtonVariant.Contained}
                    size="medium"
                    // The theme's minWidth lets a button shrink below its
                    // label, and a crowded row (Quotes' buyer selector) does.
                    sx={{ flexShrink: 0 }}
                    onMutate={group.refresh}
                  />
                )}
                {heroBeforeSearch}
              </>
            }
            afterSearch={
              <KebabMenu actions={overflowTabButtons.map((button) => ({ ...button, onMutate: group.refresh }))} />
            }
            topRight={tableActions}
            mobileActions={
              // The actions row has no room on a phone, so they ride in the
              // header beside the search icon. A server-sent button becomes
              // its own icon, since there is room for one and not for copy;
              // one that carries no icon keeps its label.
              <Stack direction="row" sx={{ alignItems: 'center' }}>
                {tableActions}
                {[...(currentTab?.buttons ?? []), ...(currentTableGroup?.buttons ?? [])].map((button, i) => (
                  <ActionButton
                    key={`mobile:${tabIndex}:${+i}`}
                    {...button}
                    size="medium"
                    asIcon
                    sx={{ color: 'inherit' }}
                    onMutate={group.refresh}
                  />
                ))}
              </Stack>
            }
          />
        )}
        <Box
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: theme.spacing(1),
            [theme.breakpoints.down('md')]: {
              overflowX: 'auto',
              whiteSpace: 'nowrap',
            },
          })}
        >
          {currentTab ? (
            currentTab.tableGroups.length > 1 && (
              <ToggleButtonGroup
                value={tableGroupIndex}
                onChange={(_, value) => {
                  if (value === null) return;
                  setActiveGroup(value);
                }}
                exclusive
                sx={(theme) => ({
                  marginTop: theme.spacing(1),
                  marginBottom: theme.spacing(0.5),
                  [theme.breakpoints.down('md')]: {
                    minWidth: '100%',
                  },
                })}
              >
                {currentTab.tableGroups.map((tableGroup, index: number) => (
                  <ToggleButton
                    key={tableGroup?.title}
                    value={index}
                    className={`${index === tableGroupIndex ? 'active' : ''}`}
                    sx={(theme) => ({
                      '&.active': {
                        background: theme.palette.info.main,
                        color: theme.palette.common.white,
                        '&:hover': {
                          background: `${theme.palette.info.dark}`,
                        },
                      },
                      padding: theme.spacing(0.75, 1.25),
                      lineHeight: 1.5,
                      color: theme.palette.text.primary,
                      minWidth: '120px',
                      [theme.breakpoints.down('md')]: {
                        flex: 1,
                        minWidth: 'auto',
                      },
                    })}
                  >
                    {tableGroup?.title}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )
          ) : (
            <Skeleton variant="rectangular" height={32} width={240} sx={(theme) => ({ marginTop: theme.spacing(1) })} />
          )}
          {/* On a phone the actions sit in the hero instead: this row is one
              line under the tabs, and a button wrapping out of it leaves a
              band of padding with nothing in it. */}
          {!isMobile && (
            <Stack
              direction="row"
              sx={{
                gap: 1,
                // Pushed right on its own rather than by space-between: a tab
                // with a single group renders no group control, and with nothing
                // to its left the actions would sit at the start of the row.
                marginLeft: 'auto',
              }}
            >
              {Boolean(currentTableGroup?.buttons?.length) && (
                <Box
                  sx={(theme) => ({
                    display: 'flex',
                    gap: theme.spacing(1),
                  })}
                >
                  {currentTableGroup?.buttons?.map((button, i) => (
                    <ActionButton
                      key={`${tabIndex}:${tableGroupIndex}:${+i}`}
                      {...button}
                      sx={{ textAlign: 'right' }}
                      onMutate={group.refresh}
                    />
                  ))}
                </Box>
              )}
            </Stack>
          )}
        </Box>
        <Box sx={(theme) => ({ paddingTop: theme.spacing(0.5) })}>
          {currentTab?.cards?.map((card, cardIndex) => {
            return <ActionAlertCard key={`${card.title}_${cardIndex} `} content={card} />;
          })}
        </Box>
        {!isLoadingTables ? (
          isEmpty ? (
            <Box sx={{ mt: '100px' }}>
              <EmptyTableState placeholder={currentTableGroup?.placeholder} searchTerm={search} onRefresh={refetch} />
            </Box>
          ) : (
            currentTableGroup && (
              <TableGroup
                screen={data}
                tableGroup={currentTableGroup}
                tabIndex={tabIndex}
                tableGroupIndex={tableGroupIndex}
                isRevalidating={result.fetching || result.stale || tab.isFetching || group.isFetching}
                onRefresh={group.refresh}
              />
            )
          )
        ) : (
          <ExpandableTablePlaceholderNew />
        )}
        {/* Under the tab's contents rather than in the row above them: an
            action like "View orders" is one the user reaches once they have
            read the list, and on a phone the end of the list is where the
            thumb already is. */}
        {!showLoading && Boolean(currentTab?.footerButtons?.length) && (
          <Stack sx={{ alignItems: 'center' }}>
            {currentTab?.footerButtons?.map((button, i) => (
              <ActionButton
                key={`tab:${tabIndex}:footer:${+i}`}
                {...button}
                size="medium"
                fullWidth={isMobile}
                sx={(theme) => ({
                  marginY: theme.spacing(2),
                  minWidth: 140,
                  backgroundColor: theme.palette.common.white,
                })}
                onMutate={tab.refresh}
              />
            ))}
          </Stack>
        )}
        {showDateRangePicker && (
          <Suspense fallback={null}>
            <DateRangePickerDialog
              open
              startDate={shownDateRange.startDate ?? dayjs()}
              endDate={shownDateRange.endDate ?? dayjs().add(1, 'day')}
              minDate={dateRange.minDate}
              onDatesChange={setDateRange}
              onClose={() => setShowDateRangePicker(false)}
            />
          </Suspense>
        )}
      </Container>
    </QueryResult>
  );
};

export default TablesScreen;
